import type { Dayjs } from "dayjs";

import { SWReactIcons } from "@similarweb/icons";
import { shortMonthAndDay, shortMonthAndYear } from "@similarweb/pro-logic-components";

import type { ISortDirection } from "app/@types/userdata";
import type { ESerpTypes } from "app/common/serp/SerpFilterMaps";
import { getMinGranularityDays } from "app/components/campaigns/shared/utils";
import { DefaultCell, DefaultCellRightAlign } from "app/components/React/Table/cells";
import type { IEnrichButtonCellProps } from "app/components/React/Table/cells/EnrichButtonCell";
import type { IconButtonCellProps } from "app/components/React/Table/cells/ExpansionButtonCell";
import { KeywordDifficultyCell } from "app/components/React/Table/cells/KeywordDifficultyCell";
import { LocationCell } from "app/components/React/Table/cells/LocationCell";
import { RowSelectionConsumer } from "app/components/React/Table/cells/RowSelection";
import { DefaultCellHeaderRightAlign } from "app/components/React/Table/headerCells";
import { SelectAllRowsHeaderCellConsumer } from "app/components/React/Table/headerCells/SelectAllRowsHeaderCell";
import { translate } from "app/filters/i18nFilter";
import { getChangeColumns } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/KeywordsReportTableChangeColumnsConfig";
import {
    cpcTableColumns,
    intentTableColumns,
    lastUpdatedTableColumns,
    topUrlTableColumns,
    urlAmountTableColumns,
} from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/KeywordsReportTableFixedColumns";
import { isSERPSnapshotButtonEnabled } from "app/pages/digital-marketing/keywords-campaigns/reports/shared/RankTrackerReportsSharedUtils/ReportsSharedUtils";
import type { IRankTrackerColumn } from "app/pages/digital-marketing/keywords-campaigns/reports/shared/Table/TableUtils/RankTrackerTablesTypes";
import {
    RTEnrichButtonCell,
    TagsOfKeywordCell,
    SerpExpansionButtonCell,
} from "app/pages/digital-marketing/keywords-campaigns/shared/components";
import type { DatesRange } from "app/pages/digital-marketing/keywords-campaigns/shared/types";
import { SerpTableCellCompare } from "app/pages/website-analysis/traffic-sources/search/components/serp/SerpTableCellCompare";
import { SerpTableCellSingle } from "app/pages/website-analysis/traffic-sources/search/components/serp/SerpTableCellSingle";
import { PreferencesService } from "app/services/preferences/preferencesService";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

import {
    filterAndSortColumnsForTable,
    getManagedColumnsConfigForTable,
} from "../../shared/Table/TableUtils/RankTrackerTableSharedUtils";

import {
    EColumnsFields,
    KEYWORDS_REPORT_COMPARE_MODE_TABLE_COLUMNS_PREFERENCES_KEY,
    KEYWORDS_REPORT_SINGLE_MODE_TABLE_COLUMNS_PREFERENCES_KEY,
} from "app/common/campaigns/KeywordsCampaginKeywordsTableConsts";
import {
    EnrichButtonContainer,
    ExpansionButtonWrapper,
    ExpansionTrendButtonWrapper,
} from "./KeywordsReportStyledComponents";
import type { ISelectedSites } from "./KeywordsReportTypes";
import { EKeywordsReportKeywordsTableMetrics } from "./KeywordsReportTypes";

const SERP_MIN_WIDTH_SINGLE = 130;
const SERP_MIN_WIDTH_COMPARE = 175;

export const getRowSelectionTableColumns = (
    trackCellSelected: (isSelected: boolean, keyword: string) => void,
    trackAllSelected: (allRowsSelected: boolean) => void,
) => {
    return {
        fixed: true,
        cellComponent: RowSelectionConsumer,
        track: (props) => trackCellSelected(!props.row.selected, props.row.keyword),
        trackAllSelected: trackAllSelected,
        sortable: false,
        headerComponent: SelectAllRowsHeaderCellConsumer,
        isResizable: false,
        width: 48,
    };
};

const expansionButtonElement = (
    <ExpansionTrendButtonWrapper>
        <SWReactIcons
            iconName={"tracker"}
            size={"xs"}
        />
    </ExpansionTrendButtonWrapper>
);

const getKeywordCellWithExpansion = (
    props: IEnrichButtonCellProps & IconButtonCellProps,
    durationObject: DatesRange,
) => {
    const isMoreThanOneDaySelected = durationObject?.to.diff(durationObject?.from, "days") > 0;
    return (
        <EnrichButtonContainer>
            <RTEnrichButtonCell
                {...props}
                isDisabled={!isMoreThanOneDaySelected}
                enabledTooltip={isMoreThanOneDaySelected}
                tooltipText={translate("campaigns.keywordsReport.trendsButton.seeTrend")}
                buttonElement={expansionButtonElement}
                presentEnrichButton={true}
            >
                <ExpansionButtonWrapper>
                    <SerpExpansionButtonCell
                        {...(props as Parameters<typeof SerpExpansionButtonCell>[0])}
                        buttonText={translate("rankTracker.keywordsReport.table.column.keyword.button")}
                        presentJustOnHover={false}
                    />
                </ExpansionButtonWrapper>
            </RTEnrichButtonCell>
        </EnrichButtonContainer>
    );
};
const getKeywordCellWithoutExpansion = (props: IconButtonCellProps) => {
    return (
        <EnrichButtonContainer>
            <SerpExpansionButtonCell
                {...props}
                buttonText={translate("rankTracker.keywordsReport.table.column.keyword.button")}
                presentJustOnHover={false}
            />
        </EnrichButtonContainer>
    );
};

export const getKeywordsReportKeywordColumn = (
    isDisabledSERPButton: boolean,
    durationObject: DatesRange,
    presentEnrichButton: boolean,
) => {
    return {
        fixed: true,
        sortable: true,
        field: EColumnsFields.KEYWORD,
        title: translate("rankTracker.keywordsReport.table.column.keyword.dashboard.title"), //required for dashboard
        showTotalCount: true,
        isDisabled: isDisabledSERPButton,
        cellComponent: (props) => {
            return presentEnrichButton
                ? getKeywordCellWithExpansion(props, durationObject)
                : getKeywordCellWithoutExpansion(props);
        },
        wrappedComponentTooltipText: translate(
            isDisabledSERPButton
                ? "rankTracker.keywordsReport.table.column.keyword.serpSnapshot.disabled.tooltip"
                : "rankTracker.keywordsReport.table.column.keyword.serpSnapshot.tooltip",
        ),
        WrappedComponent: DefaultCell,
        cellClass: "icon-button-cell",
        cellCls: "cell-without-padding", //required for dashboard
        buttonText: translate("rankTracker.keywordsReport.table.column.keyword.button"),
        displayName: translate("seo.keywordsCampaign.wizard.addingKeywordsStep.table.column.keyword"),
        width: 276,
        tooltip: translate("seo.keywordsCampaign.wizard.addingKeywordsStep.table.column.keyword.tooltip"),
        sortDirection: "desc",
        isMandatory: true,
        presentDelimiterBelow: true,
    };
};

export const getTagsTableColumn = (visible = true) => ({
    field: EColumnsFields.TAGS,
    isEditAllowed: false,
    cellComponent: TagsOfKeywordCell,
    title: translate("rankTracker.keywordsReport.table.column.tags.dashboard.title"), //required for dashboard
    displayName: translate("seo.keywordsCampaign.wizard.addingKeywordsStep.table.column.tags"),
    tooltip: translate("seo.keywordsCampaign.wizard.addingKeywordsStep.table.column.tags.tooltip"),
    width: 170,
    minWidth: 100,
    cellClass: "cell-padding-only-sides",
    cellCls: "cell-padding-only-sides", //required for dashboard
    visible,
});

export const getVolumeTableColumns = (isMandatory: boolean) => ({
    field: EColumnsFields.VOLUME,
    sortable: true,
    isMandatory,
    fixed: isMandatory,
    cellComponent: DefaultCellRightAlign,
    headerComponent: DefaultCellHeaderRightAlign,
    title: translate("rankTracker.keywordsReport.table.column.volume.dashboard.title"), //required for dashboard
    displayName: translate(`analysis.source.search.all.table.columns.volume.title`),
    tooltip: translate(`rankTracker.keywordsReport.table.columns.volume.title.tooltip`),
    minWidth: 104,
    width: 110,
    sortDirection: "desc",
});

export const getDifficultyTableColumn = (durationObject: DatesRange) => {
    return {
        field: EColumnsFields.DIFFICULTY,
        sortable: true,
        trackingName: "Keyword Difficulty",
        displayName: translate("analysis.source.search.all.table.columns.keyword.difficulty.title"),
        tooltip: translate("analysis.source.search.all.table.columns.keyword.difficulty.tooltip", {
            date: durationObject?.to.format(shortMonthAndYear),
        }),
        title: translate("rankTracker.keywordsReport.table.column.KD.dashboard.title"), //required for dashboard
        minWidth: 80,
        cellComponent: KeywordDifficultyCell,
        cellClass: "kw-difficulty-table-cell",
        cellCls: "kw-difficulty-table-cell", //required for dashboard
        sortDirection: "desc",
    };
};

export const getSerpFeaturesTableColumns = (selectedSites: ISelectedSites[]) => {
    return {
        field: EColumnsFields.SERP_FEATURES,
        displayName: translate("serp.column.name"),
        title: translate("rankTracker.keywordsReport.table.column.serp.dashboard.title"), //required for dashboard
        tooltip: translate("serp.column.tooltip"),
        cellComponent: (props) => {
            return selectedSites.length !== 1 ? (
                <SerpTableCellCompare {...props} />
            ) : (
                <SerpTableCellSingle {...props} />
            );
        },
        minWidth: selectedSites?.length === 1 ? SERP_MIN_WIDTH_SINGLE : SERP_MIN_WIDTH_COMPARE,
        width: selectedSites?.length === 1 ? SERP_MIN_WIDTH_SINGLE : SERP_MIN_WIDTH_COMPARE,
        isResizable: false,
        sortable: false,
    };
};

export const getGeoLocationTableColumns = (visible: boolean) => {
    return {
        field: EColumnsFields.GEO_LOCATION,
        sortable: false,
        cellComponent: LocationCell,
        displayName: translate("seo.keywordsCampaign.keywordsReport.table.column.location"),
        tooltip: translate("seo.keywordsCampaign.keywordsReport.table.column.location.tooltip"),
        title: translate("rankTracker.keywordsReport.table.column.location.dashboard.title"), //required for dashboard
        minWidth: 205,
        sortDirection: "desc",
        visible,
    };
};

export const getDisplayNameForFirstLastColumns = (
    durationObject: DatesRange,
    granularity: GranularityNames,
    isFirst: boolean,
    presentPrefix: boolean,
    metricID?: EKeywordsReportKeywordsTableMetrics,
): string => {
    let columnDate: string;
    const date = isFirst ? durationObject.from : durationObject.to;
    if (granularity === GranularityNames.DAILY) {
        columnDate = date.local()?.format(shortMonthAndDay);
    } else {
        const amountOfDaysForRange = getMinGranularityDays(granularity, durationObject);
        const firstDayInWeek = isFirst ? date.local() : date.local().subtract(amountOfDaysForRange, "days");
        const lastDayInWeek = isFirst ? date.local().add(amountOfDaysForRange, "days") : date.local();
        const areDatesInSameMonth = firstDayInWeek.isSame(lastDayInWeek, "month");
        columnDate = areDatesInSameMonth
            ? `${firstDayInWeek.local().format(shortMonthAndDay)}-${lastDayInWeek.format("D")}`
            : `${firstDayInWeek.local().format(shortMonthAndDay)}-${lastDayInWeek.format(shortMonthAndDay)}`;
    }

    if (presentPrefix) {
        return translate(`RT.keywordsReport.table.column.trend.${metricID}.single`, { date: columnDate });
    } else {
        return columnDate;
    }
};

const getSingleModeColumns = (
    durationObject: DatesRange,
    selectedSites: ISelectedSites[],
    tab: EKeywordsReportKeywordsTableMetrics,
    granularity: GranularityNames,
    isSingleLocation: boolean,
    isSelectable: boolean,
    onTargetUrlIconClick?: (row) => void,
    positionExcludedSerpFeatures?: readonly ESerpTypes[] | null,
) => {
    const defaultVisibleColumns = [
        getTagsTableColumn(),
        getVolumeTableColumns,
        ...getChangeColumns(selectedSites, durationObject, granularity, tab, positionExcludedSerpFeatures),
        topUrlTableColumns(onTargetUrlIconClick, isSelectable),
        urlAmountTableColumns,
        getSerpFeaturesTableColumns(selectedSites),
        intentTableColumns,
        getDifficultyTableColumn(durationObject),
    ].map((column) => ({ ...column, visible: true }));
    const nonVisibleByDefault = [
        cpcTableColumns,
        getGeoLocationTableColumns(!isSingleLocation),
        lastUpdatedTableColumns,
    ].map((column) => ({ ...column, visible: false }));
    return [...defaultVisibleColumns, ...nonVisibleByDefault];
};

const getCompareModeColumns = (
    durationObject: DatesRange,
    selectedSites: ISelectedSites[],
    tab: EKeywordsReportKeywordsTableMetrics,
    granularity: GranularityNames,
    isSingleLocation: boolean,
    isSelectable: boolean,
    onTargetUrlIconClick?: (row) => void,
    positionExcludedSerpFeatures?: readonly ESerpTypes[] | null,
) => {
    const hiddenColumns = [
        topUrlTableColumns(onTargetUrlIconClick, isSelectable),
        urlAmountTableColumns,
        intentTableColumns,
        getDifficultyTableColumn(durationObject),
        cpcTableColumns,
        getGeoLocationTableColumns(!isSingleLocation),
        lastUpdatedTableColumns,
    ].map((column) => ({ ...column, visible: false }));
    const columns = [
        getTagsTableColumn(),
        getVolumeTableColumns(false),
        ...getChangeColumns(selectedSites, durationObject, granularity, tab, positionExcludedSerpFeatures),
        getSerpFeaturesTableColumns(selectedSites),
        ...hiddenColumns,
    ];
    return columns;
};

export const getDashboardKeywordsReportTableColumns = (
    durationObject: DatesRange,
    sortedColumn: string,
    sortDirection: ISortDirection,
    selectedSites: ISelectedSites[],
    tab: EKeywordsReportKeywordsTableMetrics,
    granularity: GranularityNames,
    trackCellSelected: (isSelected: boolean, keyword: string) => void,
    trackAllSelected: (allRowsSelected: boolean) => void,
    isSelectable: boolean,
    locationsIds: string,
    lastMigrationDate: Dayjs,
    lastScrapeDate: string | null,
    onTargetUrlIconClick?: (row) => void,
    prevStateColumns?: unknown[],
    positionExcludedSerpFeatures?: readonly ESerpTypes[] | null,
): IRankTrackerColumn[] => {
    if (!selectedSites) {
        return [];
    }
    const isSingleLocation = locationsIds?.split("&")?.length === 1;
    const isSingleMode = selectedSites.length === 1;
    const presentExpansionButton = tab === EKeywordsReportKeywordsTableMetrics.POSITION;
    let savedStateColumns = prevStateColumns;
    if (!prevStateColumns) {
        const preferencesKey = isSingleMode
            ? KEYWORDS_REPORT_SINGLE_MODE_TABLE_COLUMNS_PREFERENCES_KEY
            : KEYWORDS_REPORT_COMPARE_MODE_TABLE_COLUMNS_PREFERENCES_KEY;
        savedStateColumns = PreferencesService.get(preferencesKey);
    }

    const isDisabledSERPButton = !isSERPSnapshotButtonEnabled(durationObject.to, lastMigrationDate, lastScrapeDate);
    const fixedColumns = [
        isSelectable ? getRowSelectionTableColumns(trackCellSelected, trackAllSelected) : null,
        getKeywordsReportKeywordColumn(isDisabledSERPButton, durationObject, presentExpansionButton),
    ];

    let columns;
    if (isSingleMode) {
        columns = getSingleModeColumns(
            durationObject,
            selectedSites,
            tab,
            granularity,
            isSingleLocation,
            isSelectable,
            onTargetUrlIconClick,
            positionExcludedSerpFeatures,
        );
    } else {
        columns = getCompareModeColumns(
            durationObject,
            selectedSites,
            tab,
            granularity,
            isSingleLocation,
            isSelectable,
            onTargetUrlIconClick,
            positionExcludedSerpFeatures,
        );
    }

    const sortedColumns = filterAndSortColumnsForTable(columns, savedStateColumns);
    return getManagedColumnsConfigForTable(
        [...fixedColumns, ...sortedColumns].filter(Boolean),
        savedStateColumns,
        sortedColumn,
        sortDirection,
    );
};
